import React, { Component, useEffect } from 'react'
import Helmet from 'react-helmet'
import { withAuth } from 'react-oidc-context'
import { connect } from 'react-redux'
import { BrowserRouter as Router, Route, Switch, withRouter } from 'react-router-dom'

import BottomNav from './components/BottomNav'
import ReportPrint from './components/case/ReportPrintPage'
import CaseTypeEdit from './components/case-types/CaseTypeEdit'
import CaseTypeList from './components/case-types/CaseTypeList'
import ChatDrawer from './components/chat/ChatDrawer'
import ForgotPassword from './components/ForgotPassword'
import IdleTimeoutManager from './components/IdleTimeoutManager'
import CaseTypePicker from './components/IntakeForms/CaseTypePicker'
import PrivateRoute from './components/PrivateRoute'
import ResetPassword from './components/ResetPassword'
import SignedOut from './components/SignedOut'
import SignIn from './components/SignIn'
import SSEManager from './components/SSEManager'
import TopNav from './components/TopNav'
import UserProfile from './components/UserProfile'
import AdjusterEdit from './containers/AdjusterEdit'
import AdjusterList from './containers/AdjusterList'
import CalendarPage from './containers/CalendarPage'
import CasePage from './containers/CasePage'
import Cases from './containers/Cases'
import ClientEdit from './containers/ClientEdit'
import ClientList from './containers/ClientList'
import CompleteAppointment from './containers/CompleteAppointment'
import ExportProductivityReport from './containers/ExportProductivityReport'
import Home from './containers/Home'
import IntakeForm from './containers/IntakeForm'
import Library from './containers/Library'
import LibraryActivitiesAndExpenses from './containers/LibraryActivitiesAndExpenses'
import MessagesPage from './containers/MessagesPage'
import MessageThreadPage from './containers/MessageThreadPage'
import NewAppointment from './containers/NewAppointment'
import NotificationsPage from './containers/NotificationsPage'
import ProvidersPage from './containers/ProvidersPage'
import ReviewPage from './containers/ReviewPage'
import StandaloneDocumentViewer from './containers/StandaloneDocumentViewer'
import TaskPage from './containers/TaskPage'
import TasksListPage from './containers/TasksListPage'
import TeamAdd from './containers/TeamAdd'
import TeamEdit from './containers/TeamEdit'
import TeamList from './containers/TeamList'
import TranslationServiceEdit from './containers/TranslationServiceEdit'
import TranslationServiceList from './containers/TranslationServiceList'
import TransportationServiceEdit from './containers/TransportationServiceEdit'
import TransportationServiceList from './containers/TransportationServiceList'
import { COGNITO_CLIENT_ID, COGNITO_DOMAIN } from './utils/Constants'
import { beginLoading } from './actions'

import './App.css'

const CasePageWrapper = withRouter(({ match, settings, user }) => {
  return <CasePage caseNumber={match.params.caseNumber} settings={settings} user={user} />
})

const NavigationTracker = withRouter(({ location }) => {
  useEffect(() => {
    const isCasePath = pathname => {
      const parts = pathname.split('/').filter(Boolean)
      return parts.length >= 2 && parts[0] === 'cases'
    }

    if (!isCasePath(location.pathname)) {
      sessionStorage.setItem('lastNonCaseUrl', location.pathname + location.search)
    }
  }, [location])

  return null
})

const AppLayout = withRouter(
  ({ location, currentUser, isDropdownToggled, handleOnSelect, toggleChat, isChatOpen, handleLogout, children }) => {
    const shouldShowNavigation = pathname => {
      const skipPaths = [
        '/signed-out',
        '/signin',
        '/forgot-password',
        '/reset-password',
        '/print',
        '/productivity-report',
        '/review/',
        '/shared/file/',
      ]
      return !skipPaths.some(p => pathname.includes(p))
    }

    React.useEffect(() => {
      if (isDropdownToggled) {
        handleOnSelect()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    const showNav = shouldShowNavigation(location.pathname)
    const isFullWidth =
      location.pathname.startsWith('/cases/') ||
      location.pathname.startsWith('/case/intake') ||
      location.pathname.startsWith('/appointments') ||
      location.pathname.startsWith('/tasks/') ||
      location.pathname.startsWith('/shared/file/')

    return (
      <div className="nav-container-bottom">
        {showNav && (
          <TopNav
            handleLogout={handleLogout}
            currentUser={currentUser}
            toggleChat={toggleChat}
            isChatOpen={isChatOpen}
          />
        )}
        {isFullWidth ? children : <div className={`container ${showNav ? 'nav-container' : ''}`}>{children}</div>}
        {showNav && (
          <BottomNav user={currentUser} isDropdownToggled={isDropdownToggled} handleOnSelect={handleOnSelect} />
        )}
      </div>
    )
  },
)

class App extends Component {
  constructor(props) {
    super(props)

    this.state = {
      isDropdownToggled: false,
      isChatOpen: false,
    }

    this.handleOnSelect = this.handleOnSelect.bind(this)
    this.isUserSet = this.isUserSet.bind(this)

    // Don't fetch the user profile or settings on public pages (signin, forgot-password, etc.)
    // — no token is available yet. beginLoading() will fire correctly after a successful sign-in
    // when window.location.href='/' causes a full page reload on a protected path.
    const PUBLIC_PATHS = ['/signin', '/signed-out', '/forgot-password', '/reset-password']
    if (!PUBLIC_PATHS.includes(window.location.pathname) && !window.location.pathname.startsWith('/shared/file/')) {
      this.props.beginLoading()
    }

    this.AuthElement = React.createRef()
  }

  isUserSet() {
    return (
      this.props.currentUser !== null &&
      Object.keys(this.props.currentUser).length !== 0 &&
      this.props.currentUser.roles !== null
    )
  }

  handleLogout() {
    // Force clear all OIDC keys from LocalStorage to prevent stale token persistence
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('oidc.user')) {
        localStorage.removeItem(key)
      }
    })

    // Manually redirect to Cognito Logout endpoint to avoid oidc-client-ts metadata issues
    const logoutUri = window.location.origin + '/signed-out'
    window.location.href = `${COGNITO_DOMAIN}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(
      logoutUri,
    )}`
  }

  handleOnSelect() {
    this.setState(prevState => {
      return { isDropdownToggled: !prevState.isDropdownToggled }
    })
  }

  toggleChat = () => {
    this.setState(prevState => ({ isChatOpen: !prevState.isChatOpen }))
  }

  render() {
    const { currentUser, settings, isLoading, errors } = this.props

    return (
      <div className="nav-container">
        <Helmet defaultTitle="GuideSmart" titleTemplate="GuideSmart - %s" />
        {window.location.pathname === '/signed-out' ||
        window.location.pathname === '/signin' ||
        window.location.pathname === '/forgot-password' ||
        window.location.pathname === '/reset-password' ||
        window.location.pathname.startsWith('/shared/file/') ? (
          <Router>
            <Switch>
              <Route exact path="/signed-out" component={SignedOut} />
              <Route exact path="/signin" component={SignIn} />
              <Route exact path="/forgot-password" component={ForgotPassword} />
              <Route exact path="/reset-password" component={ResetPassword} />
              <Route exact path="/shared/file/:token" component={StandaloneDocumentViewer} />
            </Switch>
          </Router>
        ) : isLoading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : errors?.some(e => e.message === 'User is not authorized to use this tenant') ? (
          <div className="text-center mt-5">
            <h3>You are not authorized to access this tenant.</h3>
            <button className="btn btn-secondary mt-3" onClick={() => this.handleLogout()}>
              Sign Out and Try Again
            </button>
          </div>
        ) : errors?.some(
            e =>
              e.message?.includes('invalid_grant') ||
              e.message?.includes('invalid grant') ||
              e.message?.includes('Unauthorized') ||
              e.message?.includes('No token available') ||
              e.message?.includes('401'),
          ) ? (
          (() => {
            const PUBLIC_PATHS = ['/signin', '/signed-out', '/forgot-password', '/reset-password']
            if (!PUBLIC_PATHS.includes(window.location.pathname)) {
              const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
              window.location.replace(`/signin?returnTo=${returnTo}`)
            }
            return null
          })()
        ) : errors?.length ? (
          errors.map((e, i) => (
            <h4 className="text-center" key={i}>
              {e.message}
            </h4>
          ))
        ) : (
          <Router>
            <NavigationTracker />
            <AppLayout
              currentUser={currentUser}
              isDropdownToggled={this.state.isDropdownToggled}
              handleOnSelect={this.handleOnSelect}
              toggleChat={this.toggleChat}
              isChatOpen={this.state.isChatOpen}
              handleLogout={this.handleLogout.bind(this)}
            >
              <Switch>
                <PrivateRoute user={currentUser} exact path="/profile">
                  <UserProfile />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/notifications">
                  <NotificationsPage />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/messages">
                  <MessagesPage currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/messages/:threadId">
                  <MessageThreadPage currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/">
                  <Home user={currentUser} settings={settings} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/calendar">
                  <CalendarPage />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library">
                  <Library user={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/team-members">
                  <TeamList currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/team-members/add">
                  <TeamAdd currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/team-members/:userId">
                  <TeamEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/activities-and-expenses">
                  <LibraryActivitiesAndExpenses />
                </PrivateRoute>
                <PrivateRoute user={currentUser} path="/library/team-members/:id?/productivity-report">
                  <ExportProductivityReport />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/treatment-providers">
                  <ProvidersPage type="Treatment" />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/equipment-providers">
                  <ProvidersPage type="Equipment" />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/pharmacies">
                  <ProvidersPage type="Pharmacy" />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/ime-providers">
                  <ProvidersPage type="IME" />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/clients">
                  <ClientList currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/clients/add">
                  <ClientEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/clients/:id">
                  <ClientEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/adjusters">
                  <AdjusterList currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/adjusters/add">
                  <AdjusterEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/adjusters/:id">
                  <AdjusterEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/translation-services">
                  <TranslationServiceList currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/translation-services/add">
                  <TranslationServiceEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/translation-services/:id">
                  <TranslationServiceEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/transportation-services">
                  <TransportationServiceList currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/transportation-services/add">
                  <TransportationServiceEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/transportation-services/:id">
                  <TransportationServiceEdit currentUser={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/case-types">
                  <CaseTypeList user={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/case-types/add">
                  <CaseTypeEdit user={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/library/case-types/:id">
                  <CaseTypeEdit user={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/case/intake/select-type">
                  <CaseTypePicker />
                </PrivateRoute>
                <PrivateRoute user={currentUser} path="/case/intake/:id?">
                  <IntakeForm user={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/providers">
                  <ProvidersPage />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/cases">
                  <Cases settings={settings} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/appointments/:appointmentNumber?">
                  <NewAppointment />
                </PrivateRoute>
                <PrivateRoute
                  user={currentUser}
                  exact
                  path="/cases/:caseNumber?/appointments/:appointmentNumber?/complete"
                >
                  <CompleteAppointment />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/cases/:caseNumber/reports/:reportNumber/print">
                  <Route render={({ match }) => <ReportPrint {...match.params} settings={settings} />} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/tasks">
                  <TasksListPage user={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} exact path="/tasks/:taskId?">
                  <TaskPage caseManager={currentUser} />
                </PrivateRoute>
                <PrivateRoute user={currentUser} path="/cases/:caseNumber">
                  <CasePageWrapper settings={settings} user={currentUser} />
                </PrivateRoute>
                <Route exact path="/review/:token" component={ReviewPage} />
                <Route exact path="/shared/file/:token" component={StandaloneDocumentViewer} />
              </Switch>
            </AppLayout>
          </Router>
        )}
        {currentUser?.id &&
          !['/signin', '/signed-out', '/forgot-password', '/reset-password'].includes(window.location.pathname) && (
            <>
              <SSEManager />
              <IdleTimeoutManager handleLogout={() => this.handleLogout()} />
              <ChatDrawer
                isOpen={this.state.isChatOpen}
                onClose={() => this.setState({ isChatOpen: false })}
                currentUser={currentUser}
              />
            </>
          )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  currentUser: state.currentUser,
  settings: state.settings,
  isLoggedIn: state.isLoggedIn,
  isLoading: state.isLoading,
  errors: state.errors,
})

const mapDispatchToProps = dispatch => ({
  beginLoading: () => dispatch(beginLoading()),
})

export default connect(mapStateToProps, mapDispatchToProps)(withAuth(App))
